import { useMemo, useState } from 'react';
import { BroadcastIcon, ClockIcon, GaugeIcon, MapPinIcon } from '@phosphor-icons/react';

import classNames from '@utils/classnames';
import { getCountryFlag } from '@utils/country-flag';
import { Card } from 'components/Card';
import { Button } from 'components/button/Button';
import { DataField } from 'components/DataField';
import { Pill } from 'components/pill/Pill';
import { OrderRouteMap } from 'components/map/OrderRouteMap';
import { ErrorState } from 'components/error-state/ErrorState';
import { SpinnerBlock } from 'components/Spinner';
import { useTranslation } from 'contexts/translation-context';

import { LIVE_TRACKING_REFRESH_MS, useTruckTracking, type TelemetryParty } from '../models';

interface ITruckTrackingPanelProps {
  supplierTruckId: string;
  party?: TelemetryParty;
  /** Panel title. Defaults to a generic "Tracking"; a panel opened under a list names the truck. */
  title?: string;
  /**
   * What to say when there is no position, instead of the default wording. That default sends the reader
   * to the integrations page to link a vehicle, which is the wrong advice inside that page itself. A
   * connection in error, or no connection at all, still speaks for itself.
   */
  emptyMessage?: string;
  /**
   * Whether the reader can change this panel's width — a side panel beside a list rather than a fixed
   * column. Then the map steps up in height as the panel is widened, instead of stretching into an ever
   * wider letterbox. Left out, the map keeps one height wherever the panel lands.
   */
  resizableWidth?: boolean;
}

/**
 * A truck's live position and telemetry.
 *
 * The server refreshes the connection's snapshot when the page asks for it, so mounting this panel is
 * what fetches a position — once. It only repeats on an interval while live tracking is on, because the
 * credentials belong to the supplier and an idle page must not spend their API quota.
 */
export const TruckTrackingPanel = ({
  supplierTruckId,
  party,
  title,
  emptyMessage,
  resizableWidth = false,
}: ITruckTrackingPanelProps) => {
  const { i18n, lang } = useTranslation();
  const [isLive, setIsLive] = useState(false);
  const { data, error, isLoading, mutate } = useTruckTracking(
    supplierTruckId,
    party,
    isLive ? LIVE_TRACKING_REFRESH_MS : 0,
  );

  const position = data?.position ?? null;

  const driverLocation = useMemo<[number, number] | null>(
    () => (position ? [position.longitude, position.latitude] : null),
    [position],
  );

  const formatMoment = (value: string | null | undefined): string => {
    if (!value) {
      return '—';
    }
    return new Date(value).toLocaleString(lang, { dateStyle: 'short', timeStyle: 'short' });
  };

  const formatOdometer = (odometer: number | null): string =>
    odometer === null ? '—' : `${Math.round(odometer).toLocaleString(lang)} km`;

  const panelTitle = title ?? i18n('liveTracking.title');

  if (isLoading && !data) {
    return (
      <Card title={panelTitle}>
        <SpinnerBlock message={i18n('liveTracking.loading')} className="min-h-32" />
      </Card>
    );
  }

  if (error && !data) {
    return (
      <Card title={panelTitle}>
        <ErrorState error={error} onRetry={() => mutate()} />
      </Card>
    );
  }

  const isMoving = position?.ignitionState === 'ON';
  // The flag and the place name come from one source — the truck's own location once a position has been
  // mirrored onto it, the payload's country before that — because a flag from one and a city from the other
  // names a place neither source claims.
  const place = data?.truckLocation
    ? [data.truckLocation.city, data.truckLocation.country].filter(Boolean).join(', ')
    : (position?.countryCode ?? '');
  const placeFlag = getCountryFlag(data?.truckLocation?.country ?? position?.countryCode);

  // Nothing came back: name the reason rather than always blaming the truck link. A connection that is
  // in error — a rejected key, say — is not the same problem as a vehicle nobody has linked yet, and
  // `emptyMessage` replaces only the wording of that last case.
  const emptyText = !data?.provider
    ? i18n('liveTracking.noConnection')
    : (data.connectionError ??
      emptyMessage ??
      `${i18n('liveTracking.noPosition')} ${i18n('liveTracking.noPositionHint')}`);

  return (
    <Card
      title={panelTitle}
      subtitle={i18n('liveTracking.subtitle')}
      rightHeading={
        <div className="flex items-center gap-2">
          {data?.provider && (
            <Pill color="primary" size="sm">
              {data.connectionLabel ?? data.provider}
            </Pill>
          )}
          {position && (
            <Pill color={isMoving ? 'positive' : 'default'} size="sm">
              {i18n(isMoving ? 'liveTracking.driving' : 'liveTracking.idle')}
            </Pill>
          )}
        </div>
      }
    >
      {/* A container query, not a viewport one: this panel is as wide as whatever column it lands in — full
          width on a truck page, a narrow side panel beside the tracking list — and the fields must follow it. */}
      <div className="@container grid gap-4">
        {position ? (
          <>
            {/* The height steps key on this grid's own width, which is the panel's — so a panel the reader
                drags wider gets a taller map rather than a wider strip of the same height. */}
            <OrderRouteMap
              routeCoordinates={[]}
              stops={[]}
              driverLocation={driverLocation}
              driverHeading={position.heading}
              className={classNames(
                'w-full rounded-lg overflow-hidden',
                resizableWidth ? 'h-72 @lg:h-80 @2xl:h-96' : 'h-72',
              )}
            />

            <div className="grid grid-cols-2 gap-4 @md:grid-cols-3">
              <DataField title={i18n('liveTracking.location')}>
                {placeFlag} {place || '—'}
              </DataField>
              <DataField title={i18n('liveTracking.speed')}>
                {position.speed === null ? '—' : `${position.speed} km/u`}
              </DataField>
              <DataField title={i18n('liveTracking.heading')}>
                {position.heading === null ? '—' : `${position.heading}°`}
              </DataField>
              <DataField title={i18n('liveTracking.odometer')}>{formatOdometer(position.odometer)}</DataField>
              <DataField title={i18n('liveTracking.fuel')}>
                {position.fuelLevel === null ? '—' : `${Math.round(position.fuelLevel)}%`}
              </DataField>
              <DataField title={i18n('liveTracking.lastMeasurement')}>{formatMoment(position.recordedAt)}</DataField>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <MapPinIcon className="w-5 h-5 shrink-0" />
            <span>{emptyText}</span>
          </div>
        )}

        {/* Kept out of the branch above on purpose: retrying and turning live tracking on is exactly what a
            user wants when nothing has come back yet, so neither control may hide behind a position. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            {isLive ? (
              <>
                <BroadcastIcon className="w-5 h-5 shrink-0 text-positive-text" weight="fill" />
                <span>{i18n('liveTracking.liveActive')}</span>
              </>
            ) : data?.lastSeenAt ? (
              <>
                <ClockIcon className="w-5 h-5 shrink-0" />
                {/* The panel knows when the truck last reported, not when it was fetched — so say that. */}
                <span>
                  {i18n('liveTracking.lastSignal')}: {formatMoment(data.lastSeenAt)}
                </span>
              </>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <Button color="ghost" onTrigger={() => mutate()} iconLeft={<GaugeIcon className="button-icon" />}>
              {i18n('liveTracking.refresh')}
            </Button>
            <Button
              color={isLive ? 'secondary' : 'primary'}
              onTrigger={() => setIsLive((previous) => !previous)}
              iconLeft={<BroadcastIcon className="button-icon" />}
            >
              {i18n(isLive ? 'liveTracking.stopLive' : 'liveTracking.startLive')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
